/**
 * Utilitaires pour l'importation de données
 * Système unifié similaire à Supabase
 */

export interface ImportColumn {
  name: string;
  labelKey: string; // Clé de traduction pour le label (ex: 'leads.name')
  type: 'text' | 'number' | 'date' | 'boolean' | 'email' | 'url';
  required: boolean;
  description?: string;
}

export interface ImportTable {
  name: string;
  displayName: string;
  columns: ImportColumn[];
  primaryKey: string;
}

export interface ParsedData {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

export interface ImportResult {
  success: number;
  errors: string[];
  warnings: string[];
}

export interface ImportPreview {
  data: any[];
  totalRows: number;
  errors: string[];
  warnings: string[];
}

/**
 * Parse CSV data from text
 */
export const parseCSV = (csvText: string): ParsedData => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) {
    return { headers: [], rows: [], totalRows: 0 };
  }

  const rawHeaders = parseCSVLine(lines[0]);
  const rows: string[][] = [];

  // Filter out empty headers and get their indices
  const validHeaderIndices: number[] = [];
  const headers: string[] = [];
  
  rawHeaders.forEach((header, index) => {
    const trimmedHeader = header.trim();
    if (trimmedHeader && trimmedHeader !== '') {
      headers.push(trimmedHeader);
      validHeaderIndices.push(index);
    }
  });

  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVLine(lines[i]);
    if (row.length >= Math.max(...validHeaderIndices) + 1) {
      // Only include columns that correspond to valid headers
      const validRow = validHeaderIndices.map(index => row[index] || '');
      rows.push(validRow);
    }
  }

  return {
    headers,
    rows,
    totalRows: rows.length
  };
};

/**
 * Parse a single CSV line handling quotes and commas
 */
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i += 2;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
      i++;
    } else {
      current += char;
      i++;
    }
  }

  // Add the last field
  result.push(current.trim());
  return result;
};

/**
 * Parse tab-separated data (from Excel/Google Sheets)
 */
export const parseTabData = (tabText: string): ParsedData => {
  const lines = tabText.trim().split('\n');
  if (lines.length < 2) {
    return { headers: [], rows: [], totalRows: 0 };
  }

  const rawHeaders = lines[0].split('\t').map(h => h.trim());
  const rows: string[][] = [];

  // Filter out empty headers and get their indices
  const validHeaderIndices: number[] = [];
  const headers: string[] = [];
  
  rawHeaders.forEach((header, index) => {
    if (header && header !== '') {
      headers.push(header);
      validHeaderIndices.push(index);
    }
  });

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('\t').map(v => v.trim());
    if (values.length >= Math.max(...validHeaderIndices) + 1) {
      // Only include columns that correspond to valid headers
      const validRow = validHeaderIndices.map(index => values[index] || '');
      rows.push(validRow);
    }
  }

  return {
    headers,
    rows,
    totalRows: rows.length
  };
};


/**
 * Validate data against table schema
 */
export const validateImportData = (
  data: ParsedData,
  table: ImportTable,
  columnMapping: Record<string, string>,
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' = 'DD/MM/YYYY'
): ImportPreview => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const processedData: any[] = [];

  // Check if all required columns are mapped
  const requiredColumns = table.columns.filter(col => col.required);
  const mappedColumns = Object.values(columnMapping);
  
  
  for (const requiredCol of requiredColumns) {
    if (!mappedColumns.includes(requiredCol.name)) {
      errors.push(`La colonne requise "${requiredCol.name}" n'est pas mappée`);
    }
  }

  // Process each row
  for (let rowIndex = 0; rowIndex < data.rows.length; rowIndex++) {
    const row = data.rows[rowIndex];
    const processedRow: any = {};

    // Initialize all columns with null values to ensure consistent order
    table.columns.forEach(col => {
      processedRow[col.name] = null;
    });

    for (const [csvColumn, dbColumn] of Object.entries(columnMapping)) {
      const columnIndex = data.headers.indexOf(csvColumn);
      if (columnIndex === -1) continue;

      const value = row[columnIndex]?.trim() || '';
      const columnDef = table.columns.find(col => col.name === dbColumn);
      
      if (!columnDef) continue;

      // Validate required fields
      if (columnDef.required && !value) {
        errors.push(`Ligne ${rowIndex + 2}: La colonne "${dbColumn}" est requise`);
        continue;
      }

      // Convert and validate value based on type
      try {
        let convertedValue = convertValue(value, columnDef.type, dateFormat);

        // Normalize optional enum-like fields
        if (dbColumn === 'direction') {
          // Only accept 'inbound' or 'outbound'; anything else becomes null
          if (convertedValue && typeof convertedValue === 'string') {
            const normalized = convertedValue.trim().toLowerCase();
            if (normalized === 'inbound' || normalized === 'outbound') {
              convertedValue = normalized;
            } else {
              convertedValue = null;
            }
          } else {
            convertedValue = null;
          }
        }
        
        // Auto-detect contact type if contact_value is provided but contact_type is not
        if (dbColumn === 'contact_value' && convertedValue && !processedRow.contact_type) {
          const contactValue = convertedValue.toString().toLowerCase();
          if (contactValue.includes('@') || contactValue.includes('email')) {
            processedRow.contact_type = 'email';
          } else if (contactValue.match(/^[+]?[0-9\s\-()]+$/)) {
            processedRow.contact_type = 'phone';
          } else if (contactValue.includes('instagram.com') || contactValue.includes('@')) {
            processedRow.contact_type = 'instagram';
          } else if (contactValue.includes('tiktok.com') || contactValue.includes('tiktok')) {
            processedRow.contact_type = 'tiktok';
          } else if (contactValue.includes('facebook.com') || contactValue.includes('facebook')) {
            processedRow.contact_type = 'facebook';
          } else if (contactValue.includes('twitter.com') || contactValue.includes('twitter') || contactValue.includes('x.com')) {
            processedRow.contact_type = 'twitter';
          } else if (contactValue.includes('linkedin.com') || contactValue.includes('linkedin')) {
            processedRow.contact_type = 'linkedin';
          } else if (contactValue.includes('youtube.com') || contactValue.includes('youtube')) {
            processedRow.contact_type = 'youtube';
          } else if (contactValue.includes('http') || contactValue.includes('www.')) {
            processedRow.contact_type = 'website';
          } else {
            processedRow.contact_type = 'other';
          }
        }
        
        // Handle empty contact fields during import (allowed for import only)
        if (dbColumn === 'contact_type' && !convertedValue) {
          // Allow null contact_type during import
          convertedValue = null;
        }
        if (dbColumn === 'contact_value' && !convertedValue) {
          // Allow null contact_value during import
          convertedValue = null;
        }
        
        // Assign values (including null for optional fields)
        if (convertedValue !== null) {
          // For date fields, convertedValue is already a Date object or null
          if (dbColumn === 'last_action_at' || dbColumn === 'next_action_at') {
            processedRow[dbColumn] = convertedValue;
          } else {
            processedRow[dbColumn] = convertedValue;
          }
        } else {
          // Handle null values for optional fields
          processedRow[dbColumn] = null;
        }
      } catch (error) {
        errors.push(`Ligne ${rowIndex + 2}: Valeur invalide pour "${dbColumn}": ${value}`);
        continue;
      }
    }

    processedData.push(processedRow);
  }

  // Migrate old contact fields if present
  const migratedData = migrateOldContactFields(processedData);
  
  return {
    data: migratedData,
    totalRows: migratedData.length,
    errors,
    warnings
  };
};

/**
 * Detect date format and convert to timestamp
 */
const detectAndConvertDate = (value: string, dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' = 'DD/MM/YYYY'): string | null => {
  if (!value || value.trim() === '') {
    return null;
  }

  const trimmedValue = value.trim();
  
  // Working date variable reused across parsing branches
  let date: Date;
  
  // Common date formats to try
  const dateFormats = [
    // ISO formats
    /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/, // YYYY-MM-DD HH:mm:ss
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO with T
    
    // French formats
    /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
    /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/, // DD/MM/YYYY HH:mm:ss
    /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
    /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/, // DD-MM-YYYY HH:mm:ss
  ];

  // Try to parse as Unix timestamp first (only if it's a pure number)
  if (/^\d+$/.test(trimmedValue)) {
    const unixTimestamp = parseInt(trimmedValue);
    if (!isNaN(unixTimestamp) && unixTimestamp > 0 && unixTimestamp < 2000000000) {
      // Valid Unix timestamp (between 1970 and 2033)
      const date = new Date(unixTimestamp * 1000);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    }
  }

  // Try to parse the date directly first (skip if empty)
  if (trimmedValue) {
    let date = new Date(trimmedValue);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  }

  // Try parsing with different formats
  for (const format of dateFormats) {
    if (format.test(trimmedValue)) {
      // Handle DD/MM/YYYY vs MM/DD/YYYY ambiguity
      if (trimmedValue.includes('/')) {
        const parts = trimmedValue.split('/');
        if (parts.length === 3) {
          const first = parseInt(parts[0]);
          const second = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          
          if (first <= 31 && second <= 12) {
            let day, month;
            
            if (dateFormat === 'DD/MM/YYYY') {
              day = first;
              month = second;
            } else if (dateFormat === 'MM/DD/YYYY') {
              day = second;
              month = first;
            } else {
              // Default to DD/MM/YYYY
              day = first;
              month = second;
            }
            
            date = new Date(year, month - 1, day);
            
            if (!isNaN(date.getTime())) {
              return date.toISOString();
            }
          }
        }
      } else if (trimmedValue.includes('-')) {
        // Handle DD-MM-YYYY vs YYYY-MM-DD
        const parts = trimmedValue.split('-');
        if (parts.length === 3) {
          const first = parseInt(parts[0]);
          const second = parseInt(parts[1]);
          const third = parseInt(parts[2]);
          
          // If first part is 4 digits, it's YYYY-MM-DD
          if (first > 31) {
            date = new Date(first, second - 1, third);
          } else {
            // DD-MM-YYYY
            date = new Date(third, second - 1, first);
          }
          
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        }
      }
    }
  }

  // If all else fails, try to parse as is (only if not empty)
  if (trimmedValue) {
    date = new Date(trimmedValue);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  }

  // Return null for empty values instead of throwing error
  return null;
};

/**
 * Convert string value to appropriate type
 */
const convertValue = (value: string, type: ImportColumn['type'], dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' = 'DD/MM/YYYY'): any => {
  if (!value) {
    return type === 'number' ? 0 : type === 'boolean' ? false : null;
  }

  switch (type) {
    case 'number':
      const num = parseFloat(value);
      if (isNaN(num)) throw new Error('Invalid number');
      return num;
    
    case 'boolean':
      const lowerValue = value.toLowerCase();
      if (['true', '1', 'yes', 'oui'].includes(lowerValue)) return true;
      if (['false', '0', 'no', 'non'].includes(lowerValue)) return false;
      throw new Error('Invalid boolean');
    
    case 'date':
      const dateValue = detectAndConvertDate(value, dateFormat);
      return dateValue || null;
    
    case 'email':
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        throw new Error('Invalid email');
      }
      return value;
    
    case 'url':
      if (!/^https?:\/\/.+/.test(value)) {
        throw new Error('Invalid URL');
      }
      return value;
    
    default:
      return value;
  }
};

/**
 * Generate column mapping suggestions based on column names
 */
export const suggestColumnMapping = (
  csvHeaders: string[],
  tableColumns: ImportColumn[]
): Record<string, string> => {
  const mapping: Record<string, string> = {};

  for (const header of csvHeaders) {
    const lowerHeader = header.toLowerCase();
    
    // Find best match
    const bestMatch = tableColumns.find(col => {
      const lowerColName = col.name.toLowerCase();
      
      return (
        lowerHeader === lowerColName ||
        lowerHeader.includes(lowerColName) ||
        lowerColName.includes(lowerHeader) ||
        // Common translations
        (lowerHeader.includes('nom') && lowerColName.includes('name')) ||
        (lowerHeader.includes('name') && lowerColName.includes('name')) ||
        // Contact value mappings (including old field names)
        (lowerHeader.includes('email') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('contact_email') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('telephone') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('contact_phone') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('phone') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('contact') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('tel') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('mobile') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('instagram') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('tiktok') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('website') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('contact_website') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('site') && lowerColName.includes('contact_value')) ||
        (lowerHeader.includes('url') && lowerColName.includes('contact_value')) ||
        // Contact type mappings
        (lowerHeader.includes('type') && lowerColName.includes('contact_type')) ||
        (lowerHeader.includes('contact_type') && lowerColName.includes('contact_type')) ||
        (lowerHeader.includes('type_contact') && lowerColName.includes('contact_type')) ||
        // Other field mappings
        (lowerHeader.includes('secteur') && lowerColName.includes('sector_id')) ||
        (lowerHeader.includes('sector') && lowerColName.includes('sector_id')) ||
        (lowerHeader.includes('statut') && lowerColName.includes('status')) ||
        (lowerHeader.includes('status') && lowerColName.includes('status')) ||
        (lowerHeader.includes('direction') && lowerColName.includes('direction')) ||
        (lowerHeader.includes('notes') && lowerColName.includes('notes')) ||
        (lowerHeader.includes('commentaires') && lowerColName.includes('notes')) ||
        (lowerHeader.includes('relances') && lowerColName.includes('followup_count')) ||
        (lowerHeader.includes('followup') && lowerColName.includes('followup_count')) ||
        (lowerHeader.includes('nb_relances') && lowerColName.includes('followup_count')) ||
        (lowerHeader.includes('derniere') && lowerColName.includes('last_action_at')) ||
        (lowerHeader.includes('last_action') && lowerColName.includes('last_action_at')) ||
        (lowerHeader.includes('prochaine') && lowerColName.includes('next_action_at')) ||
        (lowerHeader.includes('next_action') && lowerColName.includes('next_action_at'))
      );
    });

    if (bestMatch) {
      mapping[header] = bestMatch.name;
    }
  }

  return mapping;
};

/**
 * Migrate old contact fields to new contact structure
 * This function helps users who have CSV files with the old contact_email, contact_phone, contact_website fields
 */
export const migrateOldContactFields = (data: any[]): any[] => {
  return data.map(row => {
    const migratedRow = { ...row };
    
    // If we have old contact fields, consolidate them into the new structure
    if (row.contact_email || row.contact_phone || row.contact_website) {
      // Priority: email > phone > website
      if (row.contact_email) {
        migratedRow.contact_value = row.contact_email;
        migratedRow.contact_type = 'email';
      } else if (row.contact_phone) {
        migratedRow.contact_value = row.contact_phone;
        migratedRow.contact_type = 'phone';
      } else if (row.contact_website) {
        migratedRow.contact_value = row.contact_website;
        migratedRow.contact_type = 'website';
      }
      
      // Remove old fields
      delete migratedRow.contact_email;
      delete migratedRow.contact_phone;
      delete migratedRow.contact_website;
    }
    
    return migratedRow;
  });
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (errors: string[]): string => {
  if (errors.length === 0) return '';
  
  if (errors.length === 1) {
    return errors[0];
  }
  
  if (errors.length <= 5) {
    return errors.join('; ');
  }
  
  return `${errors.slice(0, 5).join('; ')} et ${errors.length - 5} autres erreurs`;
};
